import freetextSrc from "@/assets/interactive/freetext.png";
import trueFalseSrc from "@/assets/interactive/true_false.png";
import multipleChoiceSrc from "@/assets/interactive/multiple_choice.png";
import codeMultipleChoiceSrc from "@/assets/interactive/code_multiple_choice.png";
import clozeFreetextSrc from "@/assets/interactive/cloze_freetext.png";
import clozeDropdownSrc from "@/assets/interactive/cloze_dropdown.png";
import matchingDefinitionSrc from "@/assets/interactive/matching_definition.png";
import matchingSentenceSrc from "@/assets/interactive/matching_sentence.png";
import codeTrueFalseSrc from "@/assets/interactive/code_true_false.png";
import codeFreetextSrc from "@/assets/interactive/code_freetext.png";
import codeDebugSrc from "@/assets/interactive/code_debug.png";
import codeAssertionSrc from "@/assets/interactive/code_assertion.png";
import codeExplanationSrc from "@/assets/interactive/code_explanation.png";
import selfReflectionSrc from "@/assets/interactive/self_reflection.png";
import experienceReflectionSrc from "@/assets/interactive/experience_reflection.png";
import checklistSrc from "@/assets/interactive/checklist.png";
import worksheetSrc from "@/assets/interactive/worksheet.png";

/**
 * Storyboard subtypes that have a dedicated task illustration.
 * File names in `assets/interactive/` are the snake_case kind.
 */
export const INTERACTIVE_ILLUSTRATION_BY_SUBTYPE = {
  FREETEXT: "freetext",
  TRUE_FALSE: "true_false",
  MULTIPLE_CHOICE: "multiple_choice",
  CODE_MULTIPLE_CHOICE: "code_multiple_choice",
  CLOZE_FREETEXT: "cloze_freetext",
  CLOZE_DROPDOWN: "cloze_dropdown",
  MATCHING_DEFINITION: "matching_definition",
  MATCHING_SENTENCE: "matching_sentence",
  CODE_TRUE_FALSE: "code_true_false",
  CODE_FREETEXT: "code_freetext",
  CODE_DEBUG: "code_debug",
  CODE_ASSERTION: "code_assertion",
  CODE_EXPLANATION: "code_explanation",
  SELF_REFLECTION_QUESTIONS: "self_reflection",
  EXPERIENCE_REFLECTION: "experience_reflection",
  CHECKLIST: "checklist",
  WORKSHEET: "worksheet",
} as const;

export type InteractiveIllustrationSubType =
  keyof typeof INTERACTIVE_ILLUSTRATION_BY_SUBTYPE;

export type InteractiveIllustrationKind =
  (typeof INTERACTIVE_ILLUSTRATION_BY_SUBTYPE)[InteractiveIllustrationSubType];

export const INTERACTIVE_ILLUSTRATION_SRC: Record<
  InteractiveIllustrationKind,
  string
> = {
  freetext: freetextSrc,
  true_false: trueFalseSrc,
  multiple_choice: multipleChoiceSrc,
  code_multiple_choice: codeMultipleChoiceSrc,
  cloze_freetext: clozeFreetextSrc,
  cloze_dropdown: clozeDropdownSrc,
  matching_definition: matchingDefinitionSrc,
  matching_sentence: matchingSentenceSrc,
  code_true_false: codeTrueFalseSrc,
  code_freetext: codeFreetextSrc,
  code_debug: codeDebugSrc,
  code_assertion: codeAssertionSrc,
  code_explanation: codeExplanationSrc,
  self_reflection: selfReflectionSrc,
  experience_reflection: experienceReflectionSrc,
  checklist: checklistSrc,
  worksheet: worksheetSrc,
};

export const INTERACTIVE_ILLUSTRATION_ALT: Record<
  InteractiveIllustrationKind,
  string
> = {
  freetext: "Illustration zum Fragetyp Freitext",
  true_false: "Illustration zum Fragetyp Wahr oder Falsch",
  multiple_choice: "Illustration zum Fragetyp Multiple Choice",
  code_multiple_choice: "Illustration zum Fragetyp Code Multiple Choice",
  cloze_freetext: "Illustration zum Fragetyp Lückentext",
  cloze_dropdown: "Illustration zum Fragetyp Lückentext mit Auswahl",
  matching_definition: "Illustration zum Fragetyp Zuordnung (Definition)",
  matching_sentence: "Illustration zum Fragetyp Zuordnung (Satz)",
  code_true_false: "Illustration zum Fragetyp Code Wahr oder Falsch",
  code_freetext: "Illustration zum Fragetyp Code-Freitext",
  code_debug: "Illustration zum Fragetyp Code debuggen",
  code_assertion: "Illustration zum Fragetyp Code-Assertion",
  code_explanation: "Illustration zum Fragetyp Code-Erklärung",
  self_reflection: "Illustration zum Fragetyp Selbstreflexion",
  experience_reflection: "Illustration zum Fragetyp Erfahrungsreflexion",
  checklist: "Illustration zum Fragetyp Aufgabenliste",
  worksheet: "Illustration zum Fragetyp Arbeitsblatt",
};

export const illustrationKindFromSubType = (
  subType: InteractiveIllustrationSubType
): InteractiveIllustrationKind => INTERACTIVE_ILLUSTRATION_BY_SUBTYPE[subType];
